import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { X } from "lucide-react";

/**
 * Full-screen celebration overlay shown when a user marks "I got a job".
 * Plays a short Web Audio chime (no asset hosting required) and bursts confetti
 * for ~5 seconds. The parent renders this conditionally and unmounts when done.
 *
 * Props:
 *   onDone - fired once after the celebration ends so the parent can move on.
 */
export default function GotAJobCelebration({ onDone, candidateName }) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    // Confetti - staggered bursts so it feels alive, not a single pop
    const duration = 4500;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 35, spread: 360, ticks: 70, zIndex: 200 };
    const randomInRange = (min, max) => Math.random() * (max - min) + min;
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);
      const particleCount = 60 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.4), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.6, 0.9), y: Math.random() - 0.2 } });
    }, 250);

    // Sound - a small WebAudio fanfare (no asset needed, works offline)
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        const now = ctx.currentTime;
        // Simple three-note ascending major triad (C5 → E5 → G5 → C6)
        const notes = [
          { freq: 523.25, start: 0.0, dur: 0.18 },
          { freq: 659.25, start: 0.18, dur: 0.18 },
          { freq: 783.99, start: 0.36, dur: 0.22 },
          { freq: 1046.5, start: 0.6, dur: 0.6 },
        ];
        notes.forEach((n) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "triangle";
          osc.frequency.value = n.freq;
          gain.gain.setValueAtTime(0, now + n.start);
          gain.gain.linearRampToValueAtTime(0.22, now + n.start + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, now + n.start + n.dur);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now + n.start);
          osc.stop(now + n.start + n.dur + 0.05);
        });
      }
    } catch (err) {
      // Audio policy can throw on some browsers - silent fallback
      console.warn("celebration audio failed:", err);
    }

    const t = setTimeout(() => onDone?.(), duration + 1200);
    const onKey = (e) => { if (e.key === "Escape") onDone?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      clearInterval(interval);
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [onDone]);

  return (
    <div
      data-testid="got-a-job-celebration"
      onClick={(e) => { if (e.target === e.currentTarget) onDone?.(); }}
      className="fixed inset-0 z-[150] flex items-center justify-center px-6 bg-background/95 backdrop-blur-md animate-in fade-in duration-500"
    >
      <button
        data-testid="celebration-close"
        onClick={onDone}
        aria-label="Close celebration"
        className="absolute top-5 right-5 p-3 text-foreground/60 hover:text-foreground transition-colors border border-foreground/20 hover:border-foreground/60 rounded-full"
      >
        <X className="w-5 h-5" strokeWidth={1.75} />
      </button>
      <div className="text-center max-w-3xl">
        <div className="terminal-label text-primary mb-4 animate-in slide-in-from-top duration-700">[ MILESTONE.UNLOCKED ]</div>
        <h1
          data-testid="celebration-heading"
          className="font-display font-black uppercase tracking-tight text-primary leading-[0.9] mb-8 animate-in zoom-in duration-700"
          style={{ fontSize: "clamp(3.5rem, 12vw, 9rem)" }}
        >
          Congratulations!
        </h1>
        <p className="serif-mod text-2xl sm:text-3xl tracking-tight text-foreground/95 mb-6 leading-snug" style={{ fontWeight: 500 }}>
          {candidateName ? `${candidateName}, you did it.` : "You did it."}
        </p>
        <p className="text-base sm:text-lg text-foreground/75 max-w-xl mx-auto leading-relaxed mb-10">
          Every rejection, every late-night portfolio tweak, every cover-letter rewrite - it added up to this. Wishing you the absolute best in the role ahead. Go make something brilliant.
        </p>
        <p className="terminal-label text-muted-foreground mb-6">[ - THE SISYPHEAN TEAM ]</p>
        <button
          data-testid="celebration-continue"
          onClick={onDone}
          className="text-[10px] uppercase tracking-[0.2em] font-mono px-5 py-2.5 border border-foreground/40 text-foreground hover:bg-foreground hover:text-background transition-colors"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
